// src/auth/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { User } from 'oidc-client-ts';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import userManager from './authConfig';
import { SELECTED_TEST_ID_KEY } from '../context/TestContext';
import { setGtmUserId } from '../lib/gtm';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    userManager.getUser().then((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
      if (currentUser) {
        Sentry.setUser({ id: currentUser.profile.sub });
        setGtmUserId(currentUser.profile.sub);
      }
    });

    const onUserLoaded = (u: User) => {
      setUser(u);
      Sentry.setUser({ id: u.profile.sub });
      setGtmUserId(u.profile.sub);
    };
    const onUserUnloaded = () => {
      setUser(null);
      Sentry.setUser(null);
      setGtmUserId(undefined);
      queryClient.clear();
      localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
    };
    const onSilentRenewError = (_err: Error) => {
      userManager.signinRedirect();
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [queryClient]);

  const login = () => {
    sessionStorage.setItem(
      'redirectAfterLogin',
      window.location.pathname + window.location.search,
    );
    return userManager.signinRedirect();
  };

  const logout = async () => {
    queryClient.clear();
    localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
    localStorage.removeItem(SELECTED_TEST_ID_KEY);
    try {
      await userManager.signoutRedirect();
    } catch {
      await userManager.removeUser();
    }
  };

  const value = useMemo(
    () => ({ user, isLoading, login, logout }),
    [user, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
